import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ThreeDots } from "react-loader-spinner";
import { FiFileText, FiZap, FiCheckCircle, FiAlertCircle, FiTrendingUp, FiTrendingDown, FiMinus } from "react-icons/fi";
import { Line } from "react-chartjs-2";
import { getDashboardClient } from "../../services/dashboardService";
import { formatDateBR } from "../../utils/dateUtils";
import { formatNumber } from "../../utils/numberUtils";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import {
    StatsGrid,
    InfoCard,
    ChartCard,
    IconBox,
    CardContent,
    CardTitle,
    StatNumber,
    StatBadge,
    RecentActivityTable,
    LoadingContainer,
    StatusPill,
    ActionButton
} from "./DashboardPage.styles";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

const STATUS_LABELS = {
    PENDENTE: 'Pendente',
    EM_ANALISE: 'Em Análise',
    PAGO: 'Pago',
    ATRASADO: 'Atrasado',
    CANCELADO: 'Cancelado',
    CONTESTADO: 'Contestado'
};

const ClientDashboard = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);

    useEffect(() => {
        getDashboardClient(setLoading, setData);
    }, []);

    if (loading || !data) {
        return (
            <LoadingContainer>
                <ThreeDots color="#10b981" height={80} width={80} />
            </LoadingContainer>
        );
    }

    const temPendencias = (data.summary?.faturasEmAberto || 0) > 0;

    const TrendIcon = data.energyInsights?.status === 'Aumentou' ? FiTrendingUp
        : data.energyInsights?.status === 'Diminuiu' ? FiTrendingDown
            : FiMinus;

    const consumptionChartData = {
        labels: data.charts?.consumptionHistory?.labels || [],
        datasets: [
            {
                label: 'Consumo (kWh)',
                data: data.charts?.consumptionHistory?.data || [],
                borderColor: '#f59e0b',
                backgroundColor: 'rgba(245, 158, 11, 0.2)',
                tension: 0.4,
                fill: true,
                pointRadius: 4,
                pointHoverRadius: 6
            }
        ],
    };

    const consumptionChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
                labels: { usePointStyle: true, padding: 20, font: { family: "'Inter', sans-serif", size: 12 } }
            }
        },
        scales: {
            x: { grid: { display: false } },
            y: {
                beginAtZero: true,
                grid: { borderDash: [5, 5], color: '#e5e7eb' },
                ticks: { callback: (value) => `${value} kWh` }
            }
        },
    };

    return (
        <>
            <StatsGrid>
                <InfoCard>
                    <IconBox color={temPendencias ? "#ef4444" : "#10b981"}>
                        {temPendencias ? <FiAlertCircle /> : <FiCheckCircle />}
                    </IconBox>
                    <CardContent>
                        <CardTitle>Faturas em Aberto</CardTitle>

                        {temPendencias ? (
                            <>
                                <StatNumber>
                                    R$ {(data.summary?.valorPendente || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                </StatNumber>
                                <StatBadge $positive={false}>
                                    {data.summary.faturasEmAberto} fatura(s) pendente(s)
                                </StatBadge>
                                {data.summary.aguardandoLeitura > 0 && (
                                    <p style={{ fontSize: '12px', color: '#92400e', marginTop: '6px' }}>
                                        {data.summary.aguardandoLeitura} aguardando a leitura de energia (pagamento liberado após o lançamento)
                                    </p>
                                )}
                                <ActionButton onClick={() => navigate('/meus-pagamentos')}>
                                    Ver Faturas
                                </ActionButton>
                            </>
                        ) : (
                            <>
                                <StatNumber>R$ 0,00</StatNumber>
                                <StatBadge $positive={true}>Tudo pago!</StatBadge>
                                <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                                    Você não tem pendências.
                                </p>
                            </>
                        )}
                    </CardContent>
                </InfoCard>

                <InfoCard>
                    <IconBox color="#f59e0b">
                        <FiZap />
                    </IconBox>
                    <CardContent>
                        <CardTitle>Consumo do Último Mês</CardTitle>
                        <StatNumber>{formatNumber(data.energyInsights?.consumoUltimoMes || 0)} kWh</StatNumber>
                        <span style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '5px', display: 'flex', alignItems: 'center', gap: 5 }}>
                            <TrendIcon /> {data.energyInsights?.status || 'Estável'}
                        </span>
                    </CardContent>
                </InfoCard>

                <InfoCard>
                    <IconBox color="#3b82f6">
                        <FiFileText />
                    </IconBox>
                    <CardContent>
                        <CardTitle>Última Leitura</CardTitle>
                        <StatNumber>{formatNumber(data.energyInsights?.ultimaLeitura || 0)} kWh</StatNumber>
                        <span style={{ fontSize: '12px', color: '#666' }}>
                            Registrado no medidor do apartamento
                        </span>
                    </CardContent>
                </InfoCard>
            </StatsGrid>

            {data.charts?.consumptionHistory?.data?.length > 0 && (
                <ChartCard style={{ marginTop: '20px' }}>
                    <h3>Histórico de Consumo de Energia</h3>
                    <div style={{ flex: 1, minHeight: 0 }}>
                        <Line data={consumptionChartData} options={consumptionChartOptions} />
                    </div>
                </ChartCard>
            )}

            <ChartCard style={{ minHeight: 'auto', marginTop: '20px' }}>
                <h3>Próximos Pagamentos</h3>
                <RecentActivityTable>
                    <thead>
                        <tr>
                            <th>Vencimento</th>
                            <th>Valor Total</th>
                            <th>Status</th>
                            <th>Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.nextPayments?.length > 0 ? (
                            data.nextPayments.map((pag) => (
                                <tr key={pag.id}>
                                    <td data-label="Vencimento">{formatDateBR(pag.vencimento)}</td>
                                    <td data-label="Valor">R$ {(pag.valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                                    <td data-label="Status">
                                        <StatusPill status={pag.aguardandoLeitura ? 'EM_ANALISE' : pag.status}>
                                            {pag.aguardandoLeitura ? 'Aguardando leitura' : (STATUS_LABELS[pag.status] || pag.status)}
                                        </StatusPill>
                                    </td>
                                    <td data-label="Ações">
                                        <button
                                            style={{ color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 'bold' }}
                                            onClick={() => navigate(`/faturas/${pag.id}`)}
                                        >
                                            Ver Detalhes
                                        </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="4" style={{ textAlign: "center", padding: "2rem", color: "#9ca3af" }}>
                                    Nenhum pagamento pendente.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </RecentActivityTable>
            </ChartCard>
        </>
    );
};

export default ClientDashboard;
